import { ReactElement } from "react";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { styled } from "@twilio-paste/core/styling-library";

import {
  PromptSuggestionGroupBlock,
  trackingDetails,
  WiseOwlElementIds,
} from "@twiliointernal/e2-commons-shared-definitions";

import { useWiseOwlContext } from "../../../../hooks";
import styles from "./PromptSuggestions.module.css";

interface PromptSuggestionsProps {
  block: PromptSuggestionGroupBlock;
}

const ButtonWrapper = styled(Box)`
  // Prompt suggestion buttons should fill the width in mobile view
  @media only screen and (max-width: 450px) {
    width: 100%;
  }
`;

const PromptSuggestions = ({ block }: PromptSuggestionsProps): ReactElement => {
  const {
    handlers: { handleStartNewChat, handleTrackEvent },
    applicationProps: { displayName },
  } = useWiseOwlContext();

  const handlePromptTrackingEvent = (prompt: string): void => {
    if (handleTrackEvent) {
      handleTrackEvent(
        trackingDetails.wiseOwl.promptSuggestionClicked(displayName),
        {
          promptSelected: prompt,
        }
      );
    }
  };

  const handleClickPromptSuggestion = (prompt: string): void => {
    if (handleStartNewChat) {
      handleStartNewChat({ input: prompt });
    }
    handlePromptTrackingEvent(prompt);
  };

  // TODO: Flatten the categories array to get a flat array of suggestions.
  // However, the suggestions data structure needs to be updated in the backend to have a flat array of suggestions instead of categories.
  const combinedPromptSuggestions = block.values.categories
    .map((category) => {
      return category.suggestions;
    })
    .flat();

  return (
    <Box
      as="section"
      element={WiseOwlElementIds.WISE_OWL_PROMPT_SUGGESTION_GROUP_BLOCK}
      className={styles.promptSuggestionsContainer}
    >
      {combinedPromptSuggestions.map((suggestion, index) => (
        <ButtonWrapper key={`suggestion-${index}-${suggestion}`}>
          <Button
            key={`suggestion-${index}-${suggestion}`}
            variant="secondary"
            size="rounded_small"
            fullWidth
            onClick={() => handleClickPromptSuggestion(suggestion)}
          >
            {suggestion}
          </Button>
        </ButtonWrapper>
      ))}
    </Box>
  );
};

export default PromptSuggestions;
